import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A bubble map is a data visualization tool that uses circular markers (bubbles) to
          represent quantitative data on a geographic map. The size of each bubble corresponds to
          the value of the data it represents, allowing viewers to quickly compare magnitudes across
          different geographic locations. Bubble maps are ideal for displaying regional trends,
          highlighting disparities, or showcasing concentrations of specific metrics like
          population, sales, or incidents.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Map-Bubble Charts are best suited for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Highlighting Geographic Distribution:</strong> Map-bubble charts effectively
            display data points across different geographic locations, emphasizing how values vary
            spatially.
          </li>
          <li>
            <strong>Comparing Quantitative Values:</strong> By using bubble sizes to represent
            magnitudes, these charts make it easy to compare the relative significance of data
            across multiple locations.
          </li>
          <li>
            <strong>Visualizing Patterns and Trends:</strong> Map-bubble charts are ideal for
            identifying clusters, outliers, or trends across regions, such as sales performance,
            population density, or resource allocation.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          While Map - Bubble charts offer valuable insights into geographic data, they also come
          with certain limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Bubbles:</strong> When data points are closely clustered together,
            bubbles can overlap, making it difficult to distinguish between different values or
            locations, especially in densely populated areas.
          </li>
          <li>
            <strong>Size Distortion:</strong> The size of the bubbles may not always accurately
            represent the underlying data, as viewers could misinterpret the scale of differences,
            especially when values vary significantly across locations.
          </li>
          <li>
            <strong>Clarity in Large Datasets:</strong> For large datasets with numerous data
            points, Map - Bubble charts can become cluttered and less effective, as multiple bubbles
            on the same geographic area may obscure important details, reducing the clarity and
            overall usefulness of the visualization.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is essential in Map - Bubble charts to enhance clarity and convey
          information effectively. Typically, a sequential color scheme is used, where lighter
          colors represent lower values and darker colors highlight higher values, providing an
          intuitive sense of data intensity. In cases where there’s a significant midpoint, a
          diverging color palette can be applied to show variations around a central value, allowing
          for a more nuanced comparison of data points.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Map-Bubble charts are powerful tools for visualizing geographic data, but they come with
          specific challenges that must be carefully addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Bubbles in Dense Areas:</strong> In regions with high data density,
            bubbles can overlap significantly, obscuring important information and making it
            difficult to discern individual data points.
          </li>
          <li>
            <strong>Difficulty in Size Interpretation:</strong> Accurately comparing the size of
            bubbles can be challenging, especially when differences are subtle or when the
            map&apos;s scale distorts perceptions.
          </li>
          <li>
            <strong>Limited Geographic Context:</strong> Large bubbles can obscure the underlying
            map features, reducing the clarity of geographic relationships and making it harder to
            understand the context of the data.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
